import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, type FormEvent } from 'react'
import { useForm, type Resolver, type SubmitHandler } from 'react-hook-form'
import { toast } from 'sonner'

import { useTtsVoices } from '@/api/endpoints/media'
import { useIsAdmin } from '@/api/endpoints/me'
import {
  useOllamaStatus,
  useSettings,
  useUpdateSettings,
} from '@/api/endpoints/settings'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { VoiceCarousel } from '@/features/factory/subfactories/ViralShortsForm/VoiceCarousel'
import { toVoiceOption } from '@/features/factory/subfactories/ViralShortsForm/voiceConstants'
import {
  schema,
  toFormValues,
  toPayload,
  type FormValues,
  type GlobalDefaultsSection,
} from './globalDefaultsForm'
import styles from './GlobalDefaultsForm.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'
import { errorMessage } from '@/lib/errors'

export type { GlobalDefaultsSection } from './globalDefaultsForm'

const FIELD_LABELS: Record<string, string> = {
  llm_model: 'Modelo LLM',
  llm_provider: 'Provider de nuvem',
  llm_api_key: 'Chave de API',
  ollama_desktop_url: 'Ollama do desktop (URL)',
  default_language: 'Idioma padrão',
  subtitle_style: 'Estilo de legenda',
  default_tts_voice_pt: 'Narrador PT',
  default_tts_voice_en: 'Narrador EN',
  thumbnail_style: 'Estilo de thumbnail',
  discord_cost_alert_usd: 'Discord — alerta de custo (USD)',
  discord_pipeline_duration_warn_s: 'Discord — warn de duração (s)',
}

// Providers de nuvem que o llm_kit aceita. Ollama fica de fora: ele é o
// caminho local, escolhido por área em AREA_POLICIES e pelo toggle
// Local/Nuvem, não por esta tela.
const LLM_PROVIDERS = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'anthropic', label: 'Anthropic Claude' },
] as const

// O modelo é texto livre de propósito. Uma lista fixa é exatamente o que
// deixou este campo para trás: até 01/09/2026 ele oferecia três modelos
// Claude hardcoded, e depois da migração não havia como escolher um Gemini
// pela interface. Os placeholders orientam sem restringir.
const MODEL_PLACEHOLDER: Record<string, string> = {
  gemini: 'gemini-3.1-flash-lite',
  anthropic: 'claude-sonnet-4-6',
}

const LANGUAGES = [
  { value: 'pt-br', label: 'Português (BR)' },
  { value: 'en', label: 'English' },
  { value: 'both', label: 'Ambos (PT-BR + EN)' },
] as const

const SUBTITLE_STYLES = [
  { value: 'solid', label: 'Solid' },
  { value: 'neon_stroke', label: 'Neon Stroke' },
  { value: 'neon_blur', label: 'Neon Blur' },
  { value: 'karaoke', label: 'Karaokê' },
  { value: 'random', label: 'Random' },
] as const

/** Styles that fall back to the legacy MoviePy compositing path —
 *  see ``CaptionStyleCarousel`` for the same list and rationale. */
const SLOW_SUBTITLE_STYLES = new Set(['neon_blur', 'gradient_fill'])

const THUMBNAIL_STYLES = [
  { value: 'bold-highlight', label: 'Bold highlight' },
  { value: 'magazine', label: 'Magazine' },
  { value: 'ticker', label: 'Ticker' },
] as const

export interface GlobalDefaultsFormProps {
  section: GlobalDefaultsSection
}

/**
 * One tab's worth of the singleton ``global_settings`` row. Empty /
 * "Padrão" means the override is cleared on save (the field falls back to
 * the default). Pre-populates with the latest GET response and resets
 * after every successful PUT so the user sees what was actually persisted
 * (e.g. backend coerces ``"both"`` → ``null`` for some fields).
 */
export function GlobalDefaultsForm({ section }: GlobalDefaultsFormProps) {
  const settingsQuery = useSettings()
  const mutation = useUpdateSettings()
  const ollamaStatus = useOllamaStatus()
  const isAdmin = useIsAdmin()
  const { data: voicesData, isPending: voicesPending } = useTtsVoices('')
  const ptVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'pt-br')
    .map(toVoiceOption)
  const enVoices = (voicesData?.voices ?? [])
    .filter((v) => v.language === 'en')
    .map(toVoiceOption)
  const voiceProvider = voicesData?.provider ?? ''
  const voicesEmpty = !voicesPending && ptVoices.length === 0 && enVoices.length === 0
  const voicesEmptyMessage = (
    <span className={styles.hint}>
      Nenhuma voz disponível para o provider “{voiceProvider || 'desconhecido'}”.
    </span>
  )

  // Seeded from the cache when there is one, otherwise loaded once by the
  // effect below — and from then on reset only by this tab's own save. The
  // ``values`` prop (reset on every refetch) would wipe a tab's unsaved
  // edits whenever another tab saved and the shared query refetched.
  const form = useForm<FormValues>({
    resolver: zodResolver(schema) as unknown as Resolver<FormValues>,
    defaultValues: toFormValues(settingsQuery.data ?? undefined),
  })
  const loaded = useRef(settingsQuery.data !== undefined)
  useEffect(() => {
    if (loaded.current || !settingsQuery.data) return
    loaded.current = true
    form.reset(toFormValues(settingsQuery.data))
  }, [settingsQuery.data, form])

  const submit: SubmitHandler<FormValues> = (values) => {
    mutation.mutate(toPayload(values, section), {
      onSuccess: (saved) => {
        form.reset(toFormValues(saved))
        toast.success('Configurações salvas')
      },
      onError: (err) => {
        toast.error(errorMessage(err, 'Falha ao salvar'))
      },
    })
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void form.handleSubmit(submit)(e)
  }

  if (settingsQuery.isLoading) {
    return <p className={styles.statusBanner}>Carregando configurações…</p>
  }
  if (settingsQuery.isError) {
    return (
      <QueryErrorNotice
        error={settingsQuery.error}
        label="as configurações"
        onRetry={() => {
          void settingsQuery.refetch()
        }}
      />
    )
  }

  const v = form.watch()

  // A chave nunca volta do servidor; o que volta é o resumo por provider.
  const activeProvider = v.llm_provider || (settingsQuery.data?.llm_provider ?? '')
  const savedKey = (settingsQuery.data?.credentials ?? []).find(
    (c) => c.provider === activeProvider,
  )
  const keyHint = savedKey ? '•••• •••• ' + savedKey.key_last4 : 'cole a chave aqui'

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {mutation.isError ? (
        <FormErrorSummary error={mutation.error} fieldLabels={FIELD_LABELS} />
      ) : null}

      {section === 'generation' ? (
        <div className={styles.group}>
          <div className={styles.row}>
            <div className={styles.field}>
              <span className={styles.label}>Provider de nuvem</span>
              <SelectRoot
                value={v.llm_provider || '__default__'}
                onValueChange={(val) => {
                  form.setValue('llm_provider', val === '__default__' ? '' : val, {
                    shouldDirty: true,
                  })
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Padrão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Padrão</SelectItem>
                  {LLM_PROVIDERS.map((pr) => (
                    <SelectItem key={pr.value} value={pr.value}>
                      {pr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Modelo LLM</span>
              <Input
                {...form.register('llm_model')}
                placeholder={MODEL_PLACEHOLDER[v.llm_provider] ?? 'id do modelo'}
                autoComplete="off"
              />
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Chave de API</span>
              <Input
                {...form.register('llm_api_key')}
                type="password"
                autoComplete="new-password"
                placeholder={keyHint}
              />
              <span className={styles.hint}>
                {savedKey
                  ? `Guardada e cifrada (final ${savedKey.key_last4}). Deixe em branco para manter.`
                  : 'Nenhuma chave guardada para este provider.'}
              </span>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Idioma padrão</span>
              <SelectRoot
                value={v.default_language || '__default__'}
                onValueChange={(val) => {
                  form.setValue('default_language', val === '__default__' ? '' : val, {
                    shouldDirty: true,
                  })
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Padrão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__default__">Padrão</SelectItem>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {l.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="settings-ollama-desktop">
                Ollama do desktop (URL)
              </label>
              <Input
                id="settings-ollama-desktop"
                type="text"
                placeholder="Padrão (usa o Ollama local do servidor)"
                {...form.register('ollama_desktop_url')}
              />
              {/* O teste mostra o endereço do Ollama do operador: só o admin roda. */}
              {isAdmin ? (
              <div className={styles.ollamaTest}>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!v.ollama_desktop_url || ollamaStatus.isFetching}
                  onClick={() => void ollamaStatus.refetch()}
                >
                  {ollamaStatus.isFetching ? 'Testando…' : 'Testar conexão'}
                </Button>
                {ollamaStatus.data && !ollamaStatus.isFetching ? (
                  ollamaStatus.data.available ? (
                    <span className={styles.ollamaOk} role="status">
                      ✅ Desktop acessível em {ollamaStatus.data.endpoint}
                    </span>
                  ) : (
                    <span className={styles.ollamaFail} role="status">
                      ⚠️ Desktop mudo — geração cairá para {ollamaStatus.data.endpoint}
                    </span>
                  )
                ) : null}
              </div>
              ) : null}
              <span className={styles.hint}>
                Ex.: <code>http://192.168.0.10:11434</code>. Quando alcançável, o roteiro/metadata
                local roda na GPU do desktop; se estiver mudo, cai para o Ollama do servidor sem
                falhar. Deixe em branco para usar sempre o servidor.
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {section === 'videos' ? (
        <>
          {/* ------------ Aparência ------------ */}
          <div className={styles.group}>
            <h4 className={styles.groupTitle}>Aparência</h4>

            <div className={styles.row}>
              <div className={styles.field}>
                <span className={styles.label}>Estilo de legenda</span>
                <SelectRoot
                  value={v.subtitle_style || '__default__'}
                  onValueChange={(val) => {
                    form.setValue('subtitle_style', val === '__default__' ? '' : val, {
                      shouldDirty: true,
                    })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Padrão</SelectItem>
                    {SUBTITLE_STYLES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                        {SLOW_SUBTITLE_STYLES.has(s.value) ? ' (lento)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
                {SLOW_SUBTITLE_STYLES.has(v.subtitle_style) ? (
                  <span className={styles.helper} role="status">
                    ⚠️ Esse estilo cai na engine lenta (MoviePy). Geração ~30× mais demorada.
                  </span>
                ) : null}
              </div>

              <div className={styles.field}>
                <span className={styles.label}>Estilo de thumbnail</span>
                <SelectRoot
                  value={v.thumbnail_style || '__default__'}
                  onValueChange={(val) => {
                    form.setValue('thumbnail_style', val === '__default__' ? '' : val, {
                      shouldDirty: true,
                    })
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">Padrão</SelectItem>
                    {THUMBNAIL_STYLES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </div>
            </div>
          </div>

          {/* ------------ Narrador ------------ */}
          <div className={styles.group}>
            <h4 className={styles.groupTitle}>Narrador</h4>

            <div className={styles.voiceWrap}>
              <span className={styles.label}>Português</span>
              {voicesPending ? (
                <span className={styles.hint}>Carregando vozes…</span>
              ) : voicesEmpty ? (
                voicesEmptyMessage
              ) : (
                <VoiceCarousel
                  options={ptVoices}
                  value={v.default_tts_voice_pt}
                  onValueChange={(val) => {
                    form.setValue('default_tts_voice_pt', val, { shouldDirty: true })
                  }}
                />
              )}
              <span className={styles.hint}>
                Selecione um cartão para sobrescrever; deixe em branco para usar o padrão.
              </span>
            </div>

            <div className={styles.voiceWrap}>
              <span className={styles.label}>Inglês</span>
              {voicesPending ? (
                <span className={styles.hint}>Carregando vozes…</span>
              ) : voicesEmpty ? (
                voicesEmptyMessage
              ) : (
                <VoiceCarousel
                  options={enVoices}
                  value={v.default_tts_voice_en}
                  onValueChange={(val) => {
                    form.setValue('default_tts_voice_en', val, { shouldDirty: true })
                  }}
                />
              )}
            </div>
          </div>
        </>
      ) : null}

      {section === 'notifications' ? (
        <div className={styles.group}>
          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="settings-cost-alert">
                Alerta de custo (USD)
              </label>
              <Input
                id="settings-cost-alert"
                type="number"
                step={0.1}
                min={0}
                placeholder="Padrão"
                {...form.register('discord_cost_alert_usd')}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="settings-duration-warn">
                Warn de duração do pipeline (s)
              </label>
              <Input
                id="settings-duration-warn"
                type="number"
                min={0}
                placeholder="Padrão"
                {...form.register('discord_pipeline_duration_warn_s')}
              />
            </div>
          </div>
        </div>
      ) : null}

      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          disabled={mutation.isPending || !form.formState.isDirty}
          onClick={() => {
            form.reset(toFormValues(settingsQuery.data ?? undefined))
          }}
        >
          Reverter
        </Button>
        <Button type="submit" disabled={mutation.isPending || !form.formState.isDirty}>
          {mutation.isPending ? 'Salvando…' : 'Salvar'}
        </Button>
      </div>
    </form>
  )
}
